import { ArrowUpRight, ArrowDownRight, Calculator, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/format";

const incomeCategories = ["client_payment", "consultation_fee", "other_income", "refund"];
const expenseCategories = ["Office Rent", "Salaries", "Software/Tools", "Travel", "Marketing", "Equipment", "Utilities", "Consultancy", "Miscellaneous"];
const paymentMethods = ["Bank Transfer", "UPI", "Cash", "Cheque", "Online Payment"];
const clearableEntities = ["clients", "projects", "payments", "tasks", "meetings", "site_visits", "remarks", "income", "expenses", "documents"];

export const AddIncomeModal = ({ open, onOpenChange, incomeForm, setIncomeForm, clients, projects, payments, handleSubmitIncome, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-lg">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2"><ArrowUpRight className="w-5 h-5 text-[#10B981]" /> Add Income</DialogTitle>
        <DialogDescription className="text-gray-500">Record client payment or other income</DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmitIncome} className="space-y-4">
        <div>
          <Label className="text-gray-400">Category *</Label>
          <Select value={incomeForm.category} onValueChange={(value) => setIncomeForm({...incomeForm, category: value})}>
            <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              {incomeCategories.map((cat) => (<SelectItem key={cat} value={cat} className="capitalize">{cat.replace('_', ' ')}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-gray-400">Description *</Label>
          <Input value={incomeForm.description} onChange={(e) => setIncomeForm({...incomeForm, description: e.target.value})} placeholder="Payment description" className="bg-[#171717] border-[#262626]" required />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-400">Amount (₹) *</Label>
            <Input type="number" value={incomeForm.amount} onChange={(e) => setIncomeForm({...incomeForm, amount: e.target.value})} placeholder="Enter amount" className="bg-[#171717] border-[#262626]" required />
          </div>
          <div>
            <Label className="text-gray-400">Date *</Label>
            <Input type="date" value={incomeForm.date} onChange={(e) => setIncomeForm({...incomeForm, date: e.target.value})} className="bg-[#171717] border-[#262626]" required />
          </div>
        </div>

        {incomeForm.category === "client_payment" && (
          <>
            <div>
              <Label className="text-gray-400">Client</Label>
              <Select value={incomeForm.client_id} onValueChange={(value) => setIncomeForm({...incomeForm, client_id: value})}>
                <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  {clients.map((client) => (<SelectItem key={client.client_id} value={client.client_id}>{client.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400">Project</Label>
              <Select value={incomeForm.project_id} onValueChange={(value) => setIncomeForm({...incomeForm, project_id: value})}>
                <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  {projects.map((project) => (<SelectItem key={project.project_id} value={project.project_id}>{project.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400">Link to Pending Payment</Label>
              <Select value={incomeForm.payment_id} onValueChange={(value) => setIncomeForm({...incomeForm, payment_id: value})}>
                <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue placeholder="Select pending payment (optional)" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  {payments.filter(p => !incomeForm.project_id || p.project_id === incomeForm.project_id).map((payment) => (
                    <SelectItem key={payment.payment_id} value={payment.payment_id}>
                      {payment.project_name} - {payment.description} ({formatCurrency(payment.amount)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <div>
          <Label className="text-gray-400">Payment Method</Label>
          <Select value={incomeForm.payment_method} onValueChange={(value) => setIncomeForm({...incomeForm, payment_method: value})}>
            <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue placeholder="Select method" /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              {paymentMethods.map((method) => (<SelectItem key={method} value={method}>{method}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#10B981] hover:bg-[#059669]">{submitting ? "Adding..." : "Add Income"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const AddExpenseModal = ({ open, onOpenChange, expenseForm, setExpenseForm, handleSubmitExpense, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626]">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2"><ArrowDownRight className="w-5 h-5 text-red-400" /> Add Expense</DialogTitle>
        <DialogDescription className="text-gray-500">Record a business expense</DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmitExpense} className="space-y-4">
        <div>
          <Label className="text-gray-400">Category *</Label>
          <Select value={expenseForm.category} onValueChange={(value) => setExpenseForm({...expenseForm, category: value})}>
            <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              {expenseCategories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-gray-400">Description *</Label>
          <Input value={expenseForm.description} onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})} placeholder="What was this expense for?" className="bg-[#171717] border-[#262626]" required />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-400">Amount (₹) *</Label>
            <Input type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})} placeholder="Enter amount" className="bg-[#171717] border-[#262626]" required />
          </div>
          <div>
            <Label className="text-gray-400">Date *</Label>
            <Input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})} className="bg-[#171717] border-[#262626]" required />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting || !expenseForm.category} className="bg-[#EF4444] hover:bg-[#DC2626]">{submitting ? "Adding..." : "Add Expense"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const RateCalculatorModal = ({ open, onOpenChange, calcForm, setCalcForm, calcResult, handleCalculate }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-md">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2"><Calculator className="w-5 h-5 text-[#FF4500]" /> Rate Calculator</DialogTitle>
        <DialogDescription className="text-gray-500">Calculate project amounts based on rate per sqft</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-400">Area (sqft) *</Label>
            <Input
              type="number"
              value={calcForm.area_sqft}
              onChange={(e) => setCalcForm({...calcForm, area_sqft: e.target.value})}
              placeholder="e.g., 2500"
              className="bg-[#171717] border-[#262626]"
            />
          </div>
          <div>
            <Label className="text-gray-400">Rate (₹/sqft) *</Label>
            <Input
              type="number"
              value={calcForm.rate_per_sqft}
              onChange={(e) => setCalcForm({...calcForm, rate_per_sqft: e.target.value})}
              placeholder="e.g., 150"
              className="bg-[#171717] border-[#262626]"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-400">Discount %</Label>
            <Input
              type="number"
              value={calcForm.discount_percent}
              onChange={(e) => setCalcForm({...calcForm, discount_percent: e.target.value})}
              placeholder="0"
              className="bg-[#171717] border-[#262626]"
            />
          </div>
          <div>
            <Label className="text-gray-400">GST %</Label>
            <Input
              type="number"
              value={calcForm.gst_percentage}
              onChange={(e) => setCalcForm({...calcForm, gst_percentage: e.target.value})}
              placeholder="18"
              className="bg-[#171717] border-[#262626]"
            />
          </div>
        </div>

        <Button type="button" onClick={handleCalculate} className="w-full bg-[#FF4500] hover:bg-[#E65100]">
          Calculate
        </Button>

        {calcResult && (
          <div className="mt-4 p-4 bg-[#171717] rounded space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Base Amount ({calcResult.area_sqft} × ₹{calcResult.rate_per_sqft})</span>
              <span className="text-white">{formatCurrency(calcResult.base_amount)}</span>
            </div>
            {calcResult.discount_amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Discount ({calcResult.discount_percent}%)</span>
                <span className="text-red-400">-{formatCurrency(calcResult.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Final Amount</span>
              <span className="text-white">{formatCurrency(calcResult.final_amount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">GST ({calcResult.gst_percentage}%)</span>
              <span className="text-white">+{formatCurrency(calcResult.gst_amount)}</span>
            </div>
            <div className="flex justify-between text-base font-bold border-t border-[#262626] pt-2 mt-2">
              <span className="text-[#FF4500]">Total with GST</span>
              <span className="text-[#10B981]">{formatCurrency(calcResult.total_with_gst)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500 pt-1">
              <span>Effective Rate with GST</span>
              <span>₹{calcResult.effective_rate_with_gst}/sqft</span>
            </div>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export const ClearDataModal = ({ open, onOpenChange, clearForm, setClearForm, handleClearData, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-red-900/50 max-w-md" aria-describedby="clear-data-description">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400" /> Clear All Data
        </DialogTitle>
        <DialogDescription id="clear-data-description" className="text-red-400">
          This action cannot be undone. All selected data will be permanently deleted.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <Label className="text-gray-400">Select data to clear (leave empty for all)</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {clearableEntities.map((entity) => (
              <label key={entity} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearForm.entities.includes(entity)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setClearForm({...clearForm, entities: [...clearForm.entities, entity]});
                    } else {
                      setClearForm({...clearForm, entities: clearForm.entities.filter(e => e !== entity)});
                    }
                  }}
                  className="rounded border-[#262626]"
                />
                <span className="capitalize">{entity.replace('_', ' ')}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-gray-400">Enter your password *</Label>
          <Input
            type="password"
            value={clearForm.password}
            onChange={(e) => setClearForm({...clearForm, password: e.target.value})}
            placeholder="Your account password"
            className="bg-[#171717] border-[#262626]"
          />
        </div>

        <div>
          <Label className="text-gray-400">Type "DELETE ALL DATA" to confirm *</Label>
          <Input
            value={clearForm.confirm_text}
            onChange={(e) => setClearForm({...clearForm, confirm_text: e.target.value})}
            placeholder="DELETE ALL DATA"
            className="bg-[#171717] border-[#262626]"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleClearData}
            disabled={submitting || clearForm.confirm_text !== "DELETE ALL DATA" || !clearForm.password}
            className="bg-red-600 hover:bg-red-700"
            data-testid="confirm-clear-data-btn"
          >
            {submitting ? "Clearing..." : "Clear Data"}
          </Button>
        </DialogFooter>
      </div>
    </DialogContent>
  </Dialog>
);

export const ClearEverythingModal = ({ open, onOpenChange, clearEverythingForm, setClearEverythingForm, handleClearEverything, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-red-600 max-w-md" aria-describedby="clear-everything-description">
      <DialogHeader>
        <DialogTitle className="text-red-500 flex items-center gap-2 text-xl">
          <AlertTriangle className="w-6 h-6" /> DANGER: Delete Everything
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        {/* Warning Box */}
        <div className="bg-red-950/50 border border-red-600 rounded-lg p-4">
          <p id="clear-everything-description" className="text-red-400 text-sm font-semibold mb-2">
            ⚠️ THIS ACTION WILL PERMANENTLY DELETE:
          </p>
          <ul className="text-red-300 text-xs space-y-1 ml-4 list-disc">
            <li>All clients and projects</li>
            <li>All meetings and site visits</li>
            <li>All tasks and payments</li>
            <li>All income and expense records</li>
            <li>All documents and notes</li>
            <li>All email campaigns</li>
            <li>All audit logs</li>
            <li><strong className="text-red-400">ALL USER ACCOUNTS (including yours)</strong></li>
          </ul>
          <p className="text-red-500 text-xs mt-3 font-bold">
            YOU WILL BE LOGGED OUT AND CANNOT RECOVER THIS DATA!
          </p>
        </div>

        <div>
          <Label className="text-gray-400">Enter your password *</Label>
          <Input
            type="password"
            value={clearEverythingForm.password}
            onChange={(e) => setClearEverythingForm({...clearEverythingForm, password: e.target.value})}
            placeholder="Your account password"
            className="bg-[#171717] border-red-900"
          />
        </div>

        <div>
          <Label className="text-gray-400">Type "<span className="text-red-400">DELETE EVERYTHING PERMANENTLY</span>" to confirm *</Label>
          <Input
            value={clearEverythingForm.confirm_text}
            onChange={(e) => setClearEverythingForm({...clearEverythingForm, confirm_text: e.target.value})}
            placeholder="DELETE EVERYTHING PERMANENTLY"
            className="bg-[#171717] border-red-900"
          />
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setClearEverythingForm({ password: "", confirm_text: "" });
            }}
            className="border-[#262626] flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleClearEverything}
            disabled={submitting || clearEverythingForm.confirm_text !== "DELETE EVERYTHING PERMANENTLY" || !clearEverythingForm.password}
            className="bg-red-600 hover:bg-red-700 flex-1"
            data-testid="confirm-clear-everything-btn"
          >
            {submitting ? "Deleting..." : "DELETE EVERYTHING"}
          </Button>
        </DialogFooter>
      </div>
    </DialogContent>
  </Dialog>
);
